'use strict';

/**
 * Authentication flows and processes
 * Handles SSO authentication and redirect handling
 */
window.SoracomAuthFlows = (() => {

  // Constants
  const AUTH_SERVICE_RATE_LIMIT = 5000;
  const AUTH_SERVICE_TIMEOUT = 10000;

  // Get auth config from central location
  const getAuthConfig = () => window.SoracomAuth?.getAuthConfig?.();

  /**
   * Fetch authentication token from auth service
   * @param {string} endpoint - Auth service endpoint
   * @returns {Promise<Object>} Authentication data
   */
  async function fetchAuthToken(endpoint) {
    // Validate endpoint URL for security
    const validatedEndpoint = window.SoracomAuthUtils?.validateApiEndpoint?.(endpoint);
    if (!validatedEndpoint) {
      throw new Error('Invalid auth service endpoint');
    }

    const controller = new AbortController();
    // Use validated timeout to prevent injection
    const safeTimeout = window.SoracomAuthUtils?.validateTimeoutDuration?.(AUTH_SERVICE_TIMEOUT) || AUTH_SERVICE_TIMEOUT;
    const timeoutId = setTimeout(() => controller.abort(), safeTimeout);

    try {
      const response = await fetch(validatedEndpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          // 404 means no session exists - this is expected, not an error
          return null;
        } else if (response.status === 401) {
          throw new Error('Authentication required');
        } else if (response.status === 403) {
          throw new Error('Access forbidden');
        } else if (response.status >= 500) {
          throw new Error('Server error - please try again later');
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Provide user-friendly error messages
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Authentication request timed out');
        timeoutError.name = 'TimeoutError';
        if (window.SoracomAuth?.showAuthError) {
          window.SoracomAuth.showAuthError('Authentication timed out. Please check your connection and try again.');
        }
        throw timeoutError;
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        const networkError = new Error('Network error - please check your connection');
        networkError.name = 'NetworkError';
        if (window.SoracomAuth?.showAuthError) {
          window.SoracomAuth.showAuthError('Network error. Please check your internet connection.');
        }
        throw networkError;
      }

      if (window.SoracomAuth?.showAuthError) {
        if (error.message.includes('Authentication required')) {
          window.SoracomAuth.showAuthError('Please sign in to continue.');
        } else if (error.message.includes('Access forbidden')) {
          window.SoracomAuth.showAuthError('Access denied. Please check your permissions.');
        } else if (error.message.includes('Server error')) {
          window.SoracomAuth.showAuthError('Server error. Please try again in a few moments.');
        } else {
          window.SoracomAuth.showAuthError('Authentication failed. Please try again.');
        }
      }

      throw error;
    }
  }

  /**
   * Check for session cookie authentication and handle auth service callback
   * @returns {Promise<Object>|Object|null} Authentication data or promise
   */
  function checkSessionAuthentication() {
    // Get auth config once at the beginning
    const authConfig = getAuthConfig();
    if (!authConfig) {
      console.error('Auth configuration not available');
      return null;
    }

    // Check for pending auth state from sessionStorage
    const hasPendingAuthState = (() => {
      try {
        return !!sessionStorage.getItem('auth_redirect');
      } catch (e) {
        return false;
      }
    })();

    // Check if we're coming from auth service
    const isFromAuthService = (document.referrer && document.referrer.indexOf(authConfig.HOST) !== -1) || hasPendingAuthState;

    // Check if this is a fresh page load without existing auth
    const hasExistingAuth = (() => {
      try {
        return !!localStorage.getItem('soracom_auth_token');
      } catch (e) {
        return false;
      }
    })();

    // Only attempt session cookie authentication if:
    // 1. User hasn't explicitly signed out, AND
    // 2. We're from auth service OR it's a fresh page load without existing auth
    const shouldCheckSession = isFromAuthService || !hasExistingAuth;

    if (!shouldCheckSession) {
      return null;
    }

    // Clear any existing sessionStorage auth data since we're checking session cookies
    try {
      sessionStorage.removeItem('auth_redirect');
    } catch (e) {
      console.warn('Could not clear sessionStorage:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
    }

    // Rate limiting
    const now = Date.now();
    const lastAuthCall = window.lastAuthServiceCall || 0;
    if (now - lastAuthCall < AUTH_SERVICE_RATE_LIMIT) {
      return null;
    }
    window.lastAuthServiceCall = now;

    // Attempt session cookie authentication
    return fetchAuthToken(authConfig.BASE_URL + authConfig.ENDPOINTS.TOKEN)
      .then(userData => {
        if (userData && userData.apiKey && userData.token) {
          const authData = {
            operatorId: userData.operatorId,
            apiKey: userData.apiKey,
            token: userData.token,
            authType: 'sso'
          };

          // Preserve any coverage selection that was made during SSO flow
          try {
            const storedCoverage = window.SoracomAuthCoverage?.getStoredCoverage?.();
            if (storedCoverage) {
              authData.coverage = storedCoverage;
            }
          } catch (e) {
            // Silently handle localStorage errors
          }

          const isValid = window.SoracomAuthEncryption?.validateAuthData(authData);
          if (!isValid) {
            return null;
          }

          try {
            window.SoracomAuth?.storeAuth(authData);
          } catch (storeError) {
            console.error('Error storing auth data:', window.SoracomAuthUtils?.sanitizeForLogging?.(storeError?.message || storeError) || '[error details unavailable]');
            if (window.SoracomAuth?.showAuthError) {
              window.SoracomAuth.showAuthError('Failed to save authentication data. Please try signing in again.');
            }
            return null;
          }

          // Clear any existing AuthKeys temp storage since SSO takes precedence
          try {
            sessionStorage.removeItem('soracom_temp_authkeys');
          } catch (e) {
            // Silently handle error
          }

          window.SoracomAuthUI?.hideAuthOverlay();
          window.SoracomAuthUI?.updatePageProtection(authData);
          window.SoracomAuthUI?.updateSignOutVisibility(authData);

          // Update UI immediately after storing auth
          window.SoracomAuthUtils?.updateCopyButtonsState();

          // Configure coverage options and decide if panel should open
          if (window.SoracomAuthCoverage?.setCoverageTypeOptions) {
            window.SoracomAuthCoverage.setCoverageTypeOptions(authData.token);
          }

          // Open coverage panel only if no coverage is set
          setTimeout(() => {
            const currentAuth = window.SoracomAuth ? window.SoracomAuth.getAuthState() : null;
            if (!currentAuth || !currentAuth.coverage) {
              window.SoracomAuthCoverage?.ensureCoveragePanelOpen?.();
            }
          }, 100);

          if (typeof updateTryItOutButtonStates === 'function') {
            updateTryItOutButtonStates();
          }

          if (typeof applyAuth === 'function') {
            applyAuth(authData);
          }

          // Clear auth_redirect flag after processing
          setTimeout(() => {
            try {
              sessionStorage.removeItem('auth_redirect');
            } catch (e) {
              console.warn('Could not clear auth_redirect flag:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
            }
          }, 2000);

          return authData;
        } else {
          window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Auth service: incomplete credentials received');
        }
        return null;
      })
      .catch(fetchError => {
        console.error('Auth service call failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(fetchError?.message || fetchError) || '[error details unavailable]');

        if (fetchError.name === 'TimeoutError') {
          window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Authentication check timed out');
          if (window.SoracomAuth?.showAuthError) {
            window.SoracomAuth.showAuthError('Authentication timed out. Please try again.');
          }
        } else if (fetchError.name === 'NetworkError') {
          window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Network error during authentication');
          if (window.SoracomAuth?.showAuthError) {
            window.SoracomAuth.showAuthError('Network error. Please check your connection.');
          }
        } else {
          window.SoracomAuthUtils?.productionSafeLog?.('warn', window.SoracomAuthUtils?.sanitizeForLogging?.(fetchError?.message || fetchError) || 'Authentication failed');
          if (window.SoracomAuth?.showAuthError) {
            window.SoracomAuth.showAuthError('Authentication failed. Please try signing in again.');
          }
        }
        return null;
      });
  }

  // Public API
  return {
    fetchAuthToken,
    checkSessionAuthentication
  };

})();